/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import './Utilnav.scss';

/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import getConfig from '../../../config/index';
import { getAppLocale } from '@intl/adobe-locales';
import { CCHomeAppsUtil } from 'cchome-apps';
import { getEnvironment } from '../../../utils/Environment';
import Helpnav from './Helpnav';
import GrowthUtil from '../../containers/Growth/growthUtil';
import JarvisGlobalProd from '../../../utils/JarvisGlobalProd';

/* eslint-enable no-unused-vars */

/**
 * A Navbar.
 * @class
 */
interface Props {
	locale?: string;
	hidden?: boolean;
	customerSegment?: string;
	history?: Array<string>;
	profileFetchError?: boolean;
	routes?: Object;
	intl?: any;
	location: Location;
	isGrowthWorkflow?: boolean;
	shouldRenderGetHelpWidgetForCampaign?: Function;
	isLoggedOutExp?: boolean;
	isAllAppsStarter?: boolean;
	isNUJTestWorkflow?: boolean;
	isLoggedOutCampaignExp?: boolean;
	isMobilePhotoPlan?: boolean;
}

const navBarConfig = getConfig('NAVBAR');
const { utilnavChildren } = navBarConfig;
const utilnavChildKeyMapping = {
	default: ['upgrade', 'help', 'notifications', 'navProfile', 'appLauncher', 'navIcon'],
	allAppsStarter: ['help', 'navProfile', 'appLauncher'],
	loggedOutExpSignedIn: ['help', 'navProfile', 'appLauncher', 'navIcon'],
	loggedOutExpSignedOut: ['navProfile', 'help', 'appLauncher', 'navIcon'],
	loggedOutCampaignExp: ['help'],
	quickActions: ['navProfile'],
};

class Utilnav extends Component<Props> {
	utilnavJSPromise: Promise<any>;
	utilityNavConfig: any;
	utilNavParent: HTMLDivElement | null;

	updateUtilNavConfig() {
		const { customerSegment, isLoggedOutExp } = this.props;
		this.loadFiles();
		this.createInitialConfig();

		/* istanbul ignore else */
		if (customerSegment || isLoggedOutExp) {
			this.initUtilNav(customerSegment);
		}
	}

	componentDidMount() {
		this.updateUtilNavConfig();
	}

	componentDidUpdate(prevProps) {
		if (
			(prevProps.isAllAppsStarter !== this.props.isAllAppsStarter ||
				prevProps.isGrowthWorkflow !== this.props.isGrowthWorkflow) &&
			(prevProps.isNUJTestWorkflow !== this.props.isNUJTestWorkflow || !prevProps.isNUJTestWorkflow)
		) {
			this.updateUtilNavConfig();
		}
	}

	componentWillReceiveProps(nextProps) {
		const { customerSegment, profileFetchError } = nextProps;
		if (this.props.customerSegment !== customerSegment) {
			this.initUtilNav(customerSegment);
		}
		if (this.props.profileFetchError !== profileFetchError && profileFetchError) {
			this.initUtilNav();
		}
	}

	loadFiles() {
		const navBarConfig = getConfig('NAVBAR');
		const { utilNavNext } = navBarConfig;
		let { utilNavJS, utilNavCSS } = navBarConfig;

		if (utilNavNext && window.cchome.util.hasFeature('temp_enable_utilnav_91')) {
			utilNavJS = utilNavNext.JS;
			utilNavCSS = utilNavNext.CSS;
		}

		const head = document.getElementsByTagName('head')[0];
		const utilnavCSSNode = document.createElement('link') as any;
		utilnavCSSNode.rel = 'stylesheet';
		utilnavCSSNode.type = 'text/css';
		utilnavCSSNode.href = utilNavCSS;
		utilnavCSSNode.async = false;
		head.appendChild(utilnavCSSNode);

		const utilnavJSNode = document.createElement('script');
		utilnavJSNode.type = 'text/javascript';
		utilnavJSNode.src = utilNavJS;
		utilnavJSNode.async = false;
		head.appendChild(utilnavJSNode);
		this.utilnavJSPromise = new Promise((resolve) => {
			utilnavJSNode.onload = () => {
				resolve(true);
			};
		});
	}

	isJarvisDisabledForLoggedOut(isLoggedOutCampaignExp, pathname) {
		if (isLoggedOutCampaignExp) {
			return false;
		}
		if (window.cchome.util.hasFeature('temp_cch_8523_trailhead') && pathname.split('/')[3] === 'creative-cloud') {
			return false;
		}
		return true;
	}

	createInitialConfig() {
		const clientId = getConfig('CLIENT_ID');
		const {
			isAllAppsStarter,
			isLoggedOutExp,
			isNUJTestWorkflow,
			isLoggedOutCampaignExp,
			routes,
			location,
		} = this.props;
		const growthConfig = GrowthUtil.isGrowthWorkflow() ? GrowthUtil.getGrowthConfig() : undefined;
		const showJarvisForGrowth = growthConfig && growthConfig.utilNav ? growthConfig.utilNav.showJarvis : true;

		// getAppLocale's 3rd argument is the fallback if the language in our store is not accepted by UtilityNav
		const locale = getAppLocale('utilitynav', this.props.locale, 'en');

		if (utilnavChildren.help.attributes.children && !CCHomeAppsUtil.isCCHInLMS()) {
			const indexOfJarvis = utilnavChildren.help.attributes.children.indexOf(navBarConfig.jarvisConfig);
			if (
				!(
					(!isNUJTestWorkflow && isAllAppsStarter) ||
					(isLoggedOutExp && this.isJarvisDisabledForLoggedOut(isLoggedOutCampaignExp, location.pathname)) ||
					!showJarvisForGrowth
				) &&
				indexOfJarvis < 0
			) {
				navBarConfig.jarvisConfig.appid = clientId;
				navBarConfig.jarvisConfig.callbacks = {
					initCallback: CCHomeAppsUtil.jarvisInitCallback,
					analyticsCallback: (event) => {
						if (window.cchome.util.hasFeature('temp_jarvis_global_prod')) {
							const jarvisGP = new JarvisGlobalProd();
							jarvisGP.dunamisToGPData(event);
						}
					},
				};
				utilnavChildren.help.attributes.children.push(navBarConfig.jarvisConfig);
			} else if (!isNUJTestWorkflow && isAllAppsStarter && indexOfJarvis >= 0) {
				utilnavChildren.help.attributes.children.splice(indexOfJarvis, 1);
			}
		}

		// Supply notificationConfig
		utilnavChildren.notifications.attributes.notificationsConfig = {
			appID: clientId,
			imsClientID: clientId,
		};

		// Add callback for app launcher's view all apps
		utilnavChildren.appLauncher.attributes.onViewAllApps = () => {
			window.cchome.navigationOccured = true;
			if (this.props.history) {
				this.props.history.push('/apps/all');
			}
		};

		let utilNavChildKeys = utilnavChildKeyMapping.default;

		// utilNav for Growth workflows
		if (growthConfig && growthConfig.utilNav) {
			const growthUtilNavChildren = growthConfig.utilNav.children;
			if (growthUtilNavChildren instanceof Array) {
				utilNavChildKeys = growthUtilNavChildren; // growth children array in ./growth/config.js should be same as utilNavChildren
			}
		}
		if (isAllAppsStarter) {
			utilNavChildKeys = utilnavChildKeyMapping.allAppsStarter;
		}
		if (isLoggedOutExp) {
			utilnavChildren.appLauncher.attributes.onViewAllApps = undefined;
			if (isLoggedOutCampaignExp) {
				utilNavChildKeys = utilnavChildKeyMapping.loggedOutCampaignExp;
			} else if (window.adobeIMS && window.adobeIMS.isSignedInUser()) {
				utilNavChildKeys = utilnavChildKeyMapping.loggedOutExpSignedIn;
			} else {
				utilNavChildKeys = utilnavChildKeyMapping.loggedOutExpSignedOut;
			}
		}
		if (window.location.pathname.indexOf('/quick-actions') === 0) {
			utilNavChildKeys = utilnavChildKeyMapping.quickActions;
		}

		this.utilityNavConfig = {
			...navBarConfig.utilityNavConfig,
			theme: 'light',
			locale,
			analyticsContext: {
				consumer: {
					name: getConfig('APP_NAME'),
					version: getConfig('APP_VERSION'),
					platform: 'WEB',
					client_id: clientId,
					/* eslint camelcase: ["error", {properties: "never"}] */
				},
			},
			fgFeatures: [
				'UTILNAV_Apps',
				'UTILNAV_Notification',
				'UTILNAV_On',
				'UTILNAV_CCLogo',
				'UTILNAV_Profile',
				'UTILNAV_Help',
				'UTILNAV_Upgrade',
			],
			target: ReactDOM.findDOMNode(this.utilNavParent),
			children: utilNavChildKeys.map((key) => utilnavChildren[key]),
		};
	}

	getPageName() {
		const path = window.location.pathname.split('/');
		const pdpTab = path[4];
		const regex = /appfilter/i;
		switch (pdpTab) {
			case 'welcome':
				return 'Welcome';
			case 'concierge':
				return 'Help Me Choose';
			case 'all':
				return 'First App';
			case 'installation':
				return 'downloading';
		}
		if (regex.test(pdpTab)) {
			return 'Best App';
		}
	}

	initUtilNav(customerSegment?: string) {
		const { isLoggedOutCampaignExp } = this.props;
		this.utilnavJSPromise &&
			this.utilnavJSPromise.then(() => {
				this.utilityNavConfig.isEnterpriseUser =
					customerSegment && customerSegment.toLowerCase() === 'enterprise';
				if (window.UtilityNav) {
					// eslint-disable-next-line
					window.UtilityNav(this.utilityNavConfig);
					if (
						this.props.shouldRenderGetHelpWidgetForCampaign &&
						this.props.shouldRenderGetHelpWidgetForCampaign()
					) {
						if (document.getElementsByClassName('helpNavContainer') && isLoggedOutCampaignExp) {
							document.getElementsByClassName('utility-nav-wrapper')[0] &&
								document
									.getElementsByClassName('utility-nav-wrapper')[0]
									.classList.add('disableUtilNavAppPicker');
							document.getElementsByClassName('helpNavContainer')[0].classList.add('middleAlignUtilNav');
						}
					}
				}
			});
	}

	getGrowthUtilNavClass() {
		const growthConfig = GrowthUtil.isGrowthWorkflow() ? GrowthUtil.getGrowthConfig() : undefined;
		const utilNavClass =
			growthConfig && growthConfig.utilNav && growthConfig.utilNav.utilNavClass
				? ` ${growthConfig.utilNav.utilNavClass}`
				: '';
		return utilNavClass;
	}

	render() {
		return (
			<React.Fragment>
				<div
					ref={(r) => (this.utilNavParent = r)}
					className={
						this.props.hidden
							? 'hidden'
							: `utilnav-parent middleAlignUtilNav ${this.getGrowthUtilNavClass()}`
					}
				/>
				{(this.props.isLoggedOutCampaignExp || this.props.isNUJTestWorkflow) && (
					<Helpnav {...{ ...this.props, ...{ environment: getEnvironment() } }} />
				)}
			</React.Fragment>
		);
	}
}

export default Utilnav;
